import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Heavy half (cmdk dialog UI + ~45KB search index) is split off so the
 * always-mounted Header trigger stays tiny in the entry chunk. We only
 * import it once the user actually opens search (click or ⌘K).
 */
const GlobalSearchDialog = lazy(() => import('./GlobalSearchDialog'));

interface GlobalSearchProps {
  /** Render trigger in light mode (dark icon on light header). */
  lightMode?: boolean;
}

const GlobalSearch = ({ lightMode = false }: GlobalSearchProps) => {
  const [open, setOpen] = useState(false);
  // Once true we keep the dialog mounted (closed) so subsequent opens are
  // synchronous. Avoids re-fetching the chunk on every close/reopen.
  const [hasMounted, setHasMounted] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const requestOpen = useCallback(() => {
    setHasMounted(true);
    setOpen(true);
  }, []);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (open) {
          setOpen(false);
        } else {
          requestOpen();
        }
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, requestOpen]);

  // Restore focus to the trigger after the dialog closes (matches prior UX).
  const handleAfterClose = useCallback(() => {
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={requestOpen}
        className={cn(
          'relative p-2 ml-1 transition-colors rounded-sm focus:outline-none focus-visible:ring-2',
          lightMode
            ? 'text-muted-foreground hover:text-foreground hover:bg-foreground/10 focus-visible:ring-foreground/40'
            : 'text-hero-text/70 hover:text-hero-text hover:bg-hero-text/10 focus-visible:ring-hero-text/40',
        )}
        aria-label="Search museum content"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Search (⌘K)"
      >
        <Search className="h-4 w-4" />
      </button>

      {hasMounted && (
        <Suspense fallback={null}>
          <GlobalSearchDialog
            open={open}
            onOpenChange={setOpen}
            onAfterClose={handleAfterClose}
          />
        </Suspense>
      )}
    </>
  );
};

export default GlobalSearch;
